import type { QuestionnaireAuditEntry } from '@peles/shared';
import { useQuestionnaireAudit } from '../../hooks/useQuestionnaires';

const ACTION_LABELS: Record<string, string> = {
  CREATED: 'Created',
  PREFILLED: 'Prefilled from event data',
  LINK_SENT: 'Link sent',
  OPENED: 'Opened by client',
  ANSWER_CHANGED: 'Answer changed',
  SUBMITTED: 'Submitted',
  APPLIED: 'Applied to event',
};

function valueText(value: QuestionnaireAuditEntry['newValue']): string {
  if (!value || typeof value !== 'object') return '';
  return Object.entries(value)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(', ');
}

function deviceText(entry: QuestionnaireAuditEntry): string {
  const device = [entry.deviceType, entry.browser, entry.os].filter(Boolean).join(' · ');
  const location = [entry.geoCity, entry.geoRegion, entry.geoCountry].filter(Boolean).join(', ');
  return [device, location, entry.ipAddress ?? undefined].filter(Boolean).join(' — ');
}

export interface QuestionnaireAuditViewProps {
  questionnaireId: string;
}

/** Full audit trail: every open, answer change, submit — with device + location. */
export function QuestionnaireAuditView({ questionnaireId }: QuestionnaireAuditViewProps) {
  const { data: entries = [], isLoading } = useQuestionnaireAudit(questionnaireId);

  if (isLoading) {
    return <p className="text-sm text-gray-400 py-8 text-center">Loading audit trail…</p>;
  }
  if (entries.length === 0) {
    return <p className="text-sm text-gray-400 py-8 text-center">No activity yet.</p>;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <ul className="divide-y divide-gray-50">
        {entries.map((entry) => {
          const oldText = valueText(entry.oldValue);
          const newText = valueText(entry.newValue);
          return (
            <li key={entry.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">
                      {ACTION_LABELS[entry.action] ?? entry.action}
                    </span>
                    {entry.questionPrompt && (
                      <span className="text-gray-500"> — {entry.questionPrompt}</span>
                    )}
                  </p>
                  {(oldText || newText) && entry.action === 'ANSWER_CHANGED' && (
                    <p className="mt-0.5 text-xs text-gray-600">
                      {oldText ? (
                        <>
                          <span className="line-through text-gray-400">{oldText}</span>
                          {' → '}
                        </>
                      ) : null}
                      {newText || '(cleared)'}
                    </p>
                  )}
                  <p className="mt-0.5 text-xs text-gray-400">
                    {entry.actorType === 'CLIENT'
                      ? 'Client'
                      : entry.actorType === 'SYSTEM'
                        ? 'System'
                        : (entry.userName ?? 'Producer')}
                    {deviceText(entry) && <> · {deviceText(entry)}</>}
                    {entry.clientTimezone && <> · {entry.clientTimezone}</>}
                  </p>
                </div>
                <time className="shrink-0 text-xs text-gray-400">
                  {new Date(entry.createdAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </time>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
